import { MessageResourceManager } from './../../../../resources/message-resource-manager';
import { AfterViewInit, ChangeDetectionStrategy, OnChanges, Component, Input, ViewChild, ChangeDetectorRef, SimpleChanges, EventEmitter, Output, OnInit, output } from '@angular/core';
import { BasePropertyComponent } from '../base-property-component';
import { BaseEnumPropertyViewModel, EnumItemInterface, EnumPropertyViewModelItem, NEnumPropertyViewModel } from '../../../../view-models/base-type/enum-property-view-model';
import { HttpClient } from '@angular/common/http';
import { BaseOffsetPropertyViewModel, NOffsetPropertyViewModel } from '../../../../view-models/base-type/offset-property-view-model';
import { filter, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, merge } from 'rxjs';
import { BaseEnumTextBoxComponent } from '../base/base-enum-text-box/base-enum-text-box.component';
import { BaseSecurityTextBoxComponent } from '../base/base-security-text-box/base-security-text-box.component';
import { LogService, PopperHelper } from '@nts/std/utility';
import { AsyncPipe } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OverlayOptions } from 'primeng/api';
import { BaseSelectChipsBoxComponent, ChipsOptionsInterface } from '../base';
import type { UICommandInterface } from '@nts/std/interfaces';
import { ViewReadyCoordinator } from '../view-ready-coordinator';

export interface EnumOptionsInterface {
  overlayOptions?: OverlayOptions;
}

@UntilDestroy()
@Component({
    selector: 'nts-enum-text-box',
    templateUrl: './enum-text-box.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./enum-text-box.component.scss'],
    imports: [
    AsyncPipe,
    BaseEnumTextBoxComponent,
    BaseSelectChipsBoxComponent,
    BaseSecurityTextBoxComponent
]
})
export class EnumTextBoxComponent extends BasePropertyComponent<string | number> implements OnChanges, OnInit, AfterViewInit {
  @Input() override propertyViewModel: BaseEnumPropertyViewModel<string | number>;
  @Input() isDisabled = false;
  @Input() appendTo: string | HTMLDivElement = null;
  @Input() showErrorTooltip = true;
  @Input() showErrorBorder = true;
  @Input() customCommandsName = [];
  @Input() listenClickOutside = false;
  @Input() tabIndex: number;
  @Input() primaryColor = null;
  @Input() options: EnumOptionsInterface = null;
  @Input() multiselect: boolean = false;
  @Input() chipsOptions: ChipsOptionsInterface<{ code: string | number, description?: string; }> = null;
  @Input() scrollElementClass = 'layout-main scrollable-content';

  /**
   * Se true, il dropdown si apre quando si premono le frecce su/giù
   * Se false, il dropdown si apre solo quando si clicca sull'icona del dropdown, o si preme il tasto invio
   * @default true
   */
  @Input()
  get openDropdownOnUpDownArrowKey(): boolean {
    return this.openDropdownOnUpDownArrowKey$.value;
  }
  set openDropdownOnUpDownArrowKey(value: boolean) {
    this.openDropdownOnUpDownArrowKey$.next(value);
  }

  @Output() onFinishEditing = new EventEmitter();
  readonly viewReady = output<void>();


  isViewReady = false;
  private _baseSecurityTextBox: BaseSecurityTextBoxComponent | null = null;

  @ViewChild('baseSecurityTextBox', { static: false })
  set baseSecurityTextBox(component: BaseSecurityTextBoxComponent | null) {
    this._baseSecurityTextBox = component;
    this.tryEmitViewReady();
  }
  get baseSecurityTextBox(): BaseSecurityTextBoxComponent | null {
    return this._baseSecurityTextBox;
  }

  baseEnumTextBoxComponent$: BehaviorSubject<BaseEnumTextBoxComponent> = new BehaviorSubject<BaseEnumTextBoxComponent | null>(null);

  @ViewChild('baseEnumTextBox', { static: false })
  set baseEnumTextBoxComponent(component: BaseEnumTextBoxComponent | null) {
    if (component && component !== this.baseEnumTextBoxComponent$.value) {
      component.parentComponent = this;
      this.baseEnumTextBoxComponent$.next(component);
    }

    this.tryEmitViewReady();
  }

  openDropdownOnUpDownArrowKey$ = new BehaviorSubject<boolean>(true);
  value: EnumPropertyViewModelItem<string | number>;
  selectedChips: Array<string | number> = [];
  visibleValueDescriptions$ = new BehaviorSubject<EnumItemInterface<number | string>[]>([]);
  optionsListFromChipsOptions$ = new BehaviorSubject<EnumItemInterface<number | string>[]>([]);
  parentComponent: any = null;
  customCommandList: UICommandInterface[] = [];
  private readonly viewReadyCoordinator = new ViewReadyCoordinator(['self', 'child'] as const, () => this.emitViewReady());

  constructor(
    private readonly http: HttpClient, cd: ChangeDetectorRef,
  ) {
    super(cd);
  }

  ngOnInit(): void {
    if (!this.propertyViewModel) {
      throw new Error('Missing viewModel!');
    }
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.viewReadyCoordinator.markReady('self');

      if (this.isLocalReadyWithoutChildEvent()) {
        this.viewReadyCoordinator.markReady('child');
      }

      this.tryEmitViewReady();
    });
  }

  override async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes && changes['propertyViewModel'] && changes['propertyViewModel'].currentValue) {
      this.destroyOnChange$.next(true);

      const customCommandNameToUse = this.customCommandsName?.length >= 0
        ? this.customCommandsName
        : this.propertyViewModel?.customCommandsName;

      this.customCommandList = customCommandNameToUse.map(
        (commandName: string) => {
          const uiCommand = this.propertyViewModel.customCommandList.get(commandName);

          if (!uiCommand) {
            LogService.warn(
              `Comando custom pvm "${commandName}" non trovato per la property "${this.propertyViewModel?.propertyPath?.length > 0 ? (this.propertyViewModel?.propertyPath + '.') : ''}${this.propertyViewModel?.propertyName}"`);
          }

          return uiCommand;
        }
      ).filter((mustExists: UICommandInterface) => mustExists) || [];

      // offset
      if (this.propertyViewModel instanceof BaseOffsetPropertyViewModel) {
        await this.propertyViewModel.populateValueDescriptionsAsync(this.http);
      }

      this.updateVisibleValueDescriptions();
      this.initCombo();

      // Rimane in ascolto quando cambia il pvm e il valore
      merge(
        this.propertyViewModel.customGetterValueChanged,
        this.propertyViewModel.propertyViewModelChanged,
        this.propertyViewModel.modelChanged,
        this.propertyViewModel.propertyChanged.pipe(filter((args) => args.propertyName === this.propertyViewModel.bindedValuePropertyName))
      ).pipe(
        this.untilDestroyedThis(),
        takeUntil(this.destroyOnChange$)
      ).subscribe(() => {
        this.initCombo();
        this.cd.detectChanges();
      });

      // Rimane in ascolto quando cambiano le property diverse dal valore: isVisible, isEnabled, ecc
      this.propertyViewModel.propertyChanged.pipe(
        this.untilDestroyedThis(),
        takeUntil(this.destroyOnChange$),
        filter((args) => args.propertyName !== this.propertyViewModel.bindedValuePropertyName)
      ).subscribe((args) => {
        if (args.propertyName === 'visibleValueDescriptions') {
          this.updateVisibleValueDescriptions();
        }
        this.cd.detectChanges();
      });

      this.propertyViewModel.onFocusRequested.pipe(
        this.untilDestroyedThis(),
        takeUntil(this.destroyOnChange$)
      ).subscribe(() => {
        this.focus(this.propertyViewModel.hasErrors);
      });

      // è necessario il timeout per aspettare che il container eventuale abbiamo settato il parent component
      setTimeout(() => {
        // Se questo componente è contenuto dentro un altro componente non può essere il componentIstance del pvm
        if (this.parentComponent == null) {
          this.propertyViewModel.componentIstance$.next(this);
        }
      });

    }

    if (changes && changes['chipsOptions'] && changes['chipsOptions'].currentValue) {
      this.optionsListFromChipsOptions$.next(this.chipsOptions.optionsList.map(el => {
        const key = el[this.chipsOptions?.optionCodeField ?? 'code'];
        const description = (el[this.chipsOptions?.optionDescriptionField ?? 'description']?.toString()) ?? key.toString();

        return {
          key,
          description,
          descriptionExt: null,
        };
      }));
    }
  }

  get placeholder(): string {
    return MessageResourceManager.Current.getMessage('std_Enum_Nullable_DisplayName');
  }

  get input(): HTMLInputElement {
    return (
      this.propertyViewModel?.securityAccess == null ?
        this.baseEnumTextBoxComponent$.value.input :
        this.baseSecurityTextBox.input);
  }

  get isNullable() {
    return this.propertyViewModel instanceof NEnumPropertyViewModel || this.propertyViewModel instanceof NOffsetPropertyViewModel;
  }

  getTitle(): string {
    if (this.propertyViewModel.value != null && this.propertyViewModel.valueDescriptions != null) {
      const value = this.propertyViewModel.valueDescriptions.find(vd => vd.key === this.propertyViewModel.value);

      return value && value.description
        ? value.description
        : this.placeholder;
    } else {
      return this.placeholder;
    }
  }

  untilDestroyedThis(): <U>(source: Observable<U>) => Observable<U> {
    return untilDestroyed<this>(this);
  }

  updateVisibleValueDescriptions() {
    // Aggiorna la lista delle voci visibili, lasciando solo le prperty necessarie all'enum
    // per evitare di esporre riferimenti circolari non necessari (parent, ecc)
    this.visibleValueDescriptions$.next(this.propertyViewModel.visibleValueDescriptions.map((i) => {
      return { key: i.key, description: i.description, isVisible: i.isVisible, descriptionExt: i.descriptionExt };
    }));
  }

  async onBlur(): Promise<void> {
    if (!this.isDisabled && this.propertyViewModel.isEnabled) {
      if (this.value == null) {
        this.valueChange.emit(null);

        if (this.modelUpdate) {
          await this.propertyViewModel.resetValue(false);
        }
      } else {
        this.valueChange.emit(this.value.key);

        if (this.modelUpdate) {
          await this.propertyViewModel.setValueAsync(this.value.key);
        }
      }
    }
  }

  onChange(event): void {
    this.value = event.value;
    this.valueChange.emit(this.value != null ? this.value.key : null);
    if (this.modelUpdate) {
      this.propertyViewModel.value = this.value != null ? this.value.key : null;
    }
  }

  onModelChange(val: Array<string | number>) {
    if (this.selectedChips != val) {
      this.selectedChips = val ?? [];

      if (this.modelUpdate) {
        this.propertyViewModel.value = this.selectedChips?.length > 0
          ? this.encodeMultipleEnumValue(this.selectedChips as number[])
          : null;
      }
    }
  }

  showErroTooltip(): void {
    if (this.baseEnumTextBoxComponent$.value?.popperError) {
      PopperHelper.show(this.baseEnumTextBoxComponent$.value?.popperError);
    }
  }

  show() {
    this.baseEnumTextBoxComponent$.value?.show();
  }

  hide() {
    this.baseEnumTextBoxComponent$.value?.hide();
  }

  private initCombo(): void {
    if (this.multiselect) {
      this.selectedChips = this.propertyViewModel.value != null
        ? this.decodeMultipleEnumValue(this.propertyViewModel.value as number)
        : [];
    } else {
      this.value = this.propertyViewModel.valueDescriptions.find(x => x.key === this.propertyViewModel.value) ?? null;
    }
  }

  /**
   * Codifica l'array di valori enum in unico enum ([1, 2, 4] -> 0000 0111 -> 7)
   */
  private encodeMultipleEnumValue(decodedValues: Array<number>): number {
    let result = 0;

    for (const value of decodedValues) {
      result = result | (value as number);
    }

    return result;
  }

  /**
   * Decodifica l'enum che puo' assumere piu' valori contemporaneamente e restituisce l'array di valori (7 -> 0000 0111 -> [1, 2, 4])
   */
  private decodeMultipleEnumValue(encodedValue: number): Array<number> {
    const result = [];

    const key = this.chipsOptions?.optionCodeField ?? 'key';
    let allOptions = [];

    if (this.chipsOptions?.optionsList?.length > 0) {
      allOptions = this.chipsOptions.optionsList;
    } else {
      allOptions = this.visibleValueDescriptions$.value;
    }

    for (const option of allOptions) {
      if ((option[key] & encodedValue as number) == option[key]) {
        result.push(option[key]);
      }
    }

    return result;
  }

  viewReadyCheck(): void {
    this.viewReadyCoordinator.markReady('child');
    this.tryEmitViewReady();
  }

  private isLocalReadyWithoutChildEvent(): boolean {
    return this.propertyViewModel?.securityAccess != null || this.multiselect;
  }

  private canAccessRenderedChild(): boolean {
    if (this.propertyViewModel?.securityAccess != null) {
      return this.baseSecurityTextBox != null;
    }

    if (this.multiselect) {
      return true;
    }

    return this.baseEnumTextBoxComponent$.value != null;
  }

  private tryEmitViewReady(): void {
    this.viewReadyCoordinator.tryEmit(() => this.canAccessRenderedChild());
  }

  private emitViewReady(): void {
    this.isViewReady = true;
    this.viewReady.emit();
  }
}
